/**
 * DecorationsLibrary.ts — loads & resolves the decoration pack defined in
 * `/decorations/index.json`.
 *
 * Filename convention
 * -------------------
 * Every GLB under `/public/decorations/` follows
 *
 *     <type>_<kind>[_<variant>].glb
 *
 * - `type`  = scatter layer (tree, bush, flower, stone, stump, log,
 *             mushroom, grass, branch). Drives draw-call cost via the
 *             matching `layers[type]` block (density, maxCount,
 *             castsShadow, swayStrength).
 * - `kind`  = species / material discriminator. For `tree` it resolves to
 *             a biome category through `kindToCategory` and is selected
 *             via `biome.treeMix`. For props the kind is informational
 *             (every variant of a given type is equally likely).
 * - `variant` = optional free-form distinguisher so multiple GLBs of the
 *             same (type, kind) can coexist.
 *
 * World-space sizes come from each GLB's authored bbox; per-instance
 * scale is jittered around 1.0 by `layer.scaleJitter`. No height suffix
 * is needed in the filename.
 */

import { loadGlb } from "../assets/GltfLoader";
import type { GltfMeshAsset } from "../assets/GltfLoader";
import type { StaticMeshPass } from "../passes/StaticMeshPass";

export type Season = "spring" | "summer" | "autumn" | "winter";

export interface LayerConfig {
  /** Target spacing in metres between instances. */
  cellM: number;
  /** Probability that a candidate cell emits an instance. */
  density: number;
  /** Hard cap on instances of this layer. 0 disables the layer. */
  maxCount: number;
  /** 0 = no wind sway, 1 = full sway. */
  swayStrength: number;
  /** When false, the layer is excluded from the depth-only shadow pass. */
  castsShadow: boolean;
  /** Per-instance uniform scale jitter amplitude (0.3 = ±15 %). */
  scaleJitter: number;
  /** Optional target height in metres. When set, the builder scales every
   *  instance to `targetHeightM * jitter / modelHeight`. When absent the
   *  authored bbox height is used as-is (scale ≈ 1). Useful for low-poly
   *  packs whose author units are decimetres or smaller. */
  targetHeightM?: number;
  /** Optional shoreline guard (m) — see TreeScatterOptions. */
  minGroundM?: number;
  shoreRingM?: number;
  /** Optional downward bias applied to placement Y (m). Use a small
   *  positive value (≈ 0.2 m) for tall trees on slopes to hide vertex
   *  interpolation slop. Leave 0 for small props (stones, mushrooms,
   *  flowers) so they don't sink into the ground. */
  sinkM?: number;
  /** When > 0, only accept placements that have water within this
   *  radius. Use for shore-loving layers (flowers, grass). */
  nearShoreM?: number;
}

export interface DecorationsConfig {
  season?: Season;
  /** Map filename `<kind>` token → biome category id (used for trees). */
  kindToCategory: Record<string, string>;
  biomes: Record<
    string,
    {
      /** Probability weights per tree category, normalised at load. */
      treeMix: Record<string, number>;
      /** Optional per-surface remix. Keys are TerrainSurface names
       *  (e.g. "forest", "wetland", "meadow"). Each entry fully
       *  replaces `treeMix` on pixels of that surface. Surfaces not
       *  listed fall back to `treeMix`. */
      treeMixBySurface?: Record<string, Record<string, number>>;
    }
  >;
  /** Per-`type` scatter / shadow / sway settings. */
  layers: Record<string, LayerConfig>;
  /** Flat list of GLB filenames (relative to /decorations/). */
  files: string[];
}

export interface RegisteredVariant {
  /** StaticMeshPass group id. */
  meshId: string;
  /** Local-space AABB Y extent — height of the authored model in metres. */
  modelHeight: number;
  /** Local-space AABB min-Y — added to ty so the base sits on the ground. */
  modelMinY: number;
  /** Local-space AABB centre X — subtracted from tx so the trunk sits at the
   *  placement point regardless of the authored pivot offset. */
  modelCenterX: number;
  /** Local-space AABB centre Z — same as modelCenterX for the Z axis. */
  modelCenterZ: number;
}

interface TreePool {
  /** Variants in this category, e.g. all spruce GLBs. */
  variants: RegisteredVariant[];
}

interface PropPool {
  variants: RegisteredVariant[];
}

const FILENAME_RE = /^([a-z]+)_([a-z]+)(?:_([a-z0-9_]+))?\.glb$/i;

function parseFilename(
  file: string,
): { type: string; kind: string; variant: string | null } | null {
  const m = FILENAME_RE.exec(file);
  if (!m) return null;
  return {
    type: m[1]!.toLowerCase(),
    kind: m[2]!.toLowerCase(),
    variant: m[3]?.toLowerCase() ?? null,
  };
}

export class DecorationsLibrary {
  private constructor(
    public readonly config: DecorationsConfig,
    public readonly biomeName: string,
    /** Per-tree-category pool (e.g. "coniferous" → spruce variants). */
    private readonly treePools: Map<string, TreePool>,
    /** Default tree-category cumulative weights (excludes empty cats). */
    private readonly treeCumWeights: { name: string; cum: number }[],
    /** Optional per-surface cumulative weights. Falls back to default
     *  when a surface is absent. */
    private readonly treeCumBySurface: Map<
      string,
      { name: string; cum: number }[]
    >,
    /** Per-prop-type pool (e.g. "stone" → all stone variants). */
    private readonly propPools: Map<string, PropPool>,
  ) {}

  /** Layer config for the given `type`, or `null` if none defined. */
  layer(type: string): LayerConfig | null {
    return this.config.layers[type] ?? null;
  }

  /**
   * Fetch the config and load every GLB whose layer is enabled
   * (`maxCount > 0`). Files belonging to disabled layers are skipped
   * entirely (no GPU resources, no draw call).
   */
  static async load(
    staticMeshPass: StaticMeshPass,
    biomeName = "default",
    configUrl = "/decorations/index.json",
    treeMixOverride?:
      | Record<string, number>
      | Record<string, Record<string, number>>,
  ): Promise<DecorationsLibrary> {
    const res = await fetch(configUrl);
    if (!res.ok) {
      throw new Error(`DecorationsLibrary: failed to fetch ${configUrl}`);
    }
    const config = (await res.json()) as DecorationsConfig;
    const biome = config.biomes[biomeName];
    if (!biome) {
      throw new Error(`DecorationsLibrary: unknown biome "${biomeName}"`);
    }
    // Per-terrain override takes precedence over the biome's default mix.
    // The override may be flat ({cat: w}) or nested ({surface: {cat: w}}).
    // For the nested shape a "default" key supplies the fallback.
    const overrideIsNested =
      treeMixOverride !== undefined &&
      Object.values(treeMixOverride).some(
        (v) => typeof v === "object" && v !== null,
      );
    const overrideFlat = overrideIsNested
      ? undefined
      : (treeMixOverride as Record<string, number> | undefined);
    const overrideNested = overrideIsNested
      ? (treeMixOverride as Record<string, Record<string, number>>)
      : undefined;
    const effectiveTreeMix =
      overrideNested?.["default"] ?? overrideFlat ?? biome.treeMix;
    const effectiveTreeMixBySurface: Record<string, Record<string, number>> = {
      ...(biome.treeMixBySurface ?? {}),
      ...(overrideNested ?? {}),
    };
    // The "default" entry is not a real surface — it already fed effectiveTreeMix.
    delete effectiveTreeMixBySurface["default"];

    // Bucket files by type, then (for trees) by category. Skip files
    // whose layer is disabled to keep the GPU pool small.
    const treeFilesByCat = new Map<string, string[]>();
    const propFilesByType = new Map<string, string[]>();
    for (const file of config.files) {
      const parsed = parseFilename(file);
      if (!parsed) {
        console.warn(`[Decorations] cannot parse "${file}" — skipped`);
        continue;
      }
      const layer = config.layers[parsed.type];
      if (!layer || layer.maxCount <= 0) continue;
      if (parsed.type === "tree") {
        const cat = config.kindToCategory[parsed.kind];
        if (!cat) {
          console.warn(
            `[Decorations] no kindToCategory for tree kind="${parsed.kind}" (file ${file}) — skipped`,
          );
          continue;
        }
        if (!treeFilesByCat.has(cat)) treeFilesByCat.set(cat, []);
        treeFilesByCat.get(cat)!.push(file);
      } else {
        if (!propFilesByType.has(parsed.type)) {
          propFilesByType.set(parsed.type, []);
        }
        propFilesByType.get(parsed.type)!.push(file);
      }
    }

    // Helper: load one GLB, register it as a StaticMeshPass group with
    // the layer's sway + shadow flags, return a RegisteredVariant.
    async function registerOne(
      file: string,
      layer: LayerConfig,
    ): Promise<RegisteredVariant> {
      const url = `/decorations/${file}`;
      const mesh: GltfMeshAsset = await loadGlb(url);
      const meshId = `dec/${file}`;
      // Auto-skip shadow casting for very low objects regardless of the
      // layer flag — at 250 m ortho half-extent the shadow map gives ~0.12 m
      // (high) to ~0.5 m (low quality) per texel, so anything under ~0.8 m
      // tall barely produces a visible shadow yet still contributes a full
      // draw to the depth pass. Keeps the JSON honest if someone forgets
      // to flip the flag for a new prop.
      const heightM = Math.max(0.01, mesh.bbox.max[1] - mesh.bbox.min[1]);
      const SHADOW_MIN_HEIGHT_M = 1.0;
      const castsShadow = layer.castsShadow && heightM >= SHADOW_MIN_HEIGHT_M;
      staticMeshPass.addGroup(meshId, mesh, layer.swayStrength, {
        castsShadow,
        // Foliage stays on the bias-less foliage shadow pipeline: with
        // negative depthBias small tree tris fall off the light frustum
        // and the canopy stops casting ground shadow.
        castsHardShadow: false,
        receivesShadow: false,
      });
      return {
        meshId,
        modelHeight: heightM,
        modelMinY: mesh.bbox.min[1],
        modelCenterX: (mesh.bbox.min[0] + mesh.bbox.max[0]) * 0.5,
        modelCenterZ: (mesh.bbox.min[2] + mesh.bbox.max[2]) * 0.5,
      };
    }

    // Parallel load for all tree categories.
    const treeJobs = [...treeFilesByCat.entries()].map(async ([cat, files]) => {
      const layer = config.layers["tree"]!;
      const variants = await Promise.all(
        files.map((f) => registerOne(f, layer)),
      );
      return [cat, variants] as const;
    });
    const propJobs = [...propFilesByType.entries()].map(
      async ([type, files]) => {
        const layer = config.layers[type]!;
        const variants = await Promise.all(
          files.map((f) => registerOne(f, layer)),
        );
        return [type, variants] as const;
      },
    );

    const treePools = new Map<string, TreePool>();
    for (const [cat, variants] of await Promise.all(treeJobs)) {
      if (variants.length > 0) treePools.set(cat, { variants });
    }
    const propPools = new Map<string, PropPool>();
    for (const [type, variants] of await Promise.all(propJobs)) {
      if (variants.length > 0) propPools.set(type, { variants });
    }

    // Build cumulative tree-category weights (skip cats without files or
    // with mix=0). All-trees-disabled config is allowed (treeMix may be
    // empty if you only want props).
    const buildCum = (
      mix: Record<string, number>,
    ): { name: string; cum: number }[] => {
      const cum: { name: string; cum: number }[] = [];
      let total = 0;
      for (const [cat, w] of Object.entries(mix)) {
        if (!treePools.has(cat) || w <= 0) continue;
        total += w;
        cum.push({ name: cat, cum: total });
      }
      if (total > 0) for (const e of cum) e.cum /= total;
      return cum;
    };
    const treeCumWeights = buildCum(effectiveTreeMix);
    const treeCumBySurface = new Map<string, { name: string; cum: number }[]>();
    for (const [surface, mix] of Object.entries(effectiveTreeMixBySurface)) {
      const cum = buildCum(mix);
      if (cum.length > 0) treeCumBySurface.set(surface, cum);
    }

    const totalFiles =
      [...treePools.values()].reduce((s, p) => s + p.variants.length, 0) +
      [...propPools.values()].reduce((s, p) => s + p.variants.length, 0);
    console.log(
      `[Decorations] loaded ${totalFiles} GLBs ` +
        `(trees: ${treePools.size} cats, props: ${propPools.size} types, biome="${biomeName}")`,
    );

    return new DecorationsLibrary(
      config,
      biomeName,
      treePools,
      treeCumWeights,
      treeCumBySurface,
      propPools,
    );
  }

  /**
   * Deterministic tree pick: hash (x, z) into a category (by treeMix)
   * and a variant within that category (uniform). When `surface` is
   * supplied and a per-surface mix is configured for it, that mix wins;
   * otherwise the biome's default mix is used.
   */
  pickTree(x: number, z: number, surface?: string): RegisteredVariant | null {
    const table =
      (surface ? this.treeCumBySurface.get(surface) : undefined) ??
      this.treeCumWeights;
    if (table.length === 0) return null;
    const hCat = hashUnit(x * 0.7331 + 11.13, z * 0.131 - 5.91);
    const cat = pickByCum(table, hCat) ?? table[0]!;
    const pool = this.treePools.get(cat.name);
    if (!pool || pool.variants.length === 0) return null;
    const hVar = hashUnit(x * 1.913 + 3.7, z * 0.297 + 9.4);
    const idx = Math.min(
      pool.variants.length - 1,
      Math.floor(hVar * pool.variants.length),
    );
    return pool.variants[idx]!;
  }

  /**
   * Deterministic prop pick: uniform among the variants of the given
   * type. Returns null if the type has no enabled GLBs.
   */
  pickProp(type: string, x: number, z: number): RegisteredVariant | null {
    const pool = this.propPools.get(type);
    if (!pool || pool.variants.length === 0) return null;
    // Per-type hash salts so different layers don't pick the same
    // variant index at coincident grid cells.
    const salt = type.charCodeAt(0) * 7.13 + (type.charCodeAt(1) ?? 0) * 1.7;
    const h = hashUnit(x * 1.317 + salt, z * 0.811 + salt * 0.31);
    const idx = Math.min(
      pool.variants.length - 1,
      Math.floor(h * pool.variants.length),
    );
    return pool.variants[idx]!;
  }
}

// -----------------------------------------------------------------------------
// Hashing helpers (stable across reloads — drives deterministic placement).
// -----------------------------------------------------------------------------

function hashUnit(a: number, b: number): number {
  const v = Math.sin(a * 12.9898 + b * 78.233) * 43758.5453;
  return v - Math.floor(v);
}

function pickByCum(
  table: { name: string; cum: number }[],
  u: number,
): { name: string; cum: number } | null {
  for (const e of table) {
    if (u <= e.cum) return e;
  }
  return null;
}
